<template>
  <div class="block max-w-full max-h-full">
    <div
      :class="playerClasses"
      data-test="video"
    />
  </div>
</template>

<script>
import { defineComponent, ref, reactive, computed } from 'vue';
import { loadScript } from '@/utils/loadScript';

export default defineComponent({
  props: {
    mediaId: {
      type: String,
      required: true,
    },
    autoplay: {
      type: Boolean,
      required: false,
      default: false,
    },
    muted: {
      type: Boolean,
      required: false,
      default: false,
    },
    showControls: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
    * Set what this video should do when it reaches the end
    * 'default' - Stays on the last frame
    * 'reset'   - Displays thumbnail
    * 'loop'    - Loop the video
    */
    endBehavior: {
      type: String,
      required: false,
      default: 'reset',
      validator(value) {
        return ['default', 'reset', 'loop'].includes(value);
      },
    },
    altText: {
      type: String,
      required: false,
      default: '',
    },
  },
  setup(props) {
    loadScript('//fast.wistia.com/assets/external/E-v1.js');

    // Wistia handler
    const wistia = ref(null);

    /**
     * Options for the player
     *
     * @see https://docs.wistia.com/docs/embed-options-and-plugins#options
     */
    const config = reactive({
      autoPlay: props.autoplay,
      controlsVisibleOnLoad: false,
      wmode: 'transparent',
      seo: false,
      smallPlayButton: props.showControls,
      fitStrategy: 'contain',
      playbar: props.showControls,
      fullscreenButton: props.showControls,
      volumeControl: props.showControls,
      playbackRateControl: props.showControls,
      qualityControl: false,
      settingsControl: false,
      endVideoBehavior: props.endBehavior,
      copyLinkAndThumbnailEnabled: false,
      silentAutoPlay: false,
      muted: props.muted, // video will not autoplay inline if muted is not true
      thumbnailAltText: props.altText,
      videoFoam: true,
    });

    /**
     * Dynamically set the options for the video as CSS classes so that
     * Wistia does the heavy lifting
     *
     * @see https://docs.wistia.com/docs/embed-options-and-plugins#setting-options-on-standard-and-popover-embeds
     */
    const playerClasses = computed(() => {
      // eslint-disable-next-line arrow-body-style
      const configClasses = Object.keys(config).map((key) => {
        /**
         * encoding the value so that spaces are encoded and wistia understands it
         */
        return `${key}=${encodeURIComponent(config[key])}`;
      });

      configClasses.push(`wistia_embed wistia_async_${props.mediaId}`);

      return configClasses;
    });

    function play() {
      if (wistia.value) {
        wistia.value.play();
      }
    }

    function pause() {
      if (wistia.value) {
        wistia.value.pause();
      }
    }

    /**
     * Initialise the API handler and bind to the wistia
     * player in order to be able to expose additional
     * functionality
     */
    function initWistia() {
      // eslint-disable-next-line no-underscore-dangle
      window._wq = window._wq || [];
      // eslint-disable-next-line no-undef
      _wq.push({
        id: props.mediaId,
        onReady: (video) => {
          wistia.value = video;
        },
      });
    }

    initWistia();

    return {
      playerClasses,
      play,
      pause,
    };
  },
});
</script>
